import { StoreSeller, CartItem, PaymentMethod, PaymentMethodLabel } from '../../models';
import { Button } from '../../components/ui/Button';
import { formatCurrency } from '../../utils/currency';
import { GuestInfo, PaymentEntry } from './types';

interface ConfirmStepProps {
  seller: StoreSeller;
  paymentEntries: PaymentEntry[];
  total: number;
  observation: string;
  onObservationChange: (v: string) => void;
  items: CartItem[];
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  guestInfo?: GuestInfo;
}

export function ConfirmStep({
  seller,
  paymentEntries,
  total,
  observation,
  onObservationChange,
  items,
  onBack,
  onSubmit,
  submitting,
  guestInfo,
}: ConfirmStepProps) {
  return (
    <div>
      <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
        <h2 className="text-sm font-semibold text-text-dark mb-3">Resumo do pedido</h2>
        <div className="flex flex-col gap-2 mb-3">
          {items.map((item) => (
            <div key={`${item.productId}-${item.variantId}`} className="flex justify-between gap-2 text-sm">
              <span className="text-text-medium min-w-0 break-words">
                {item.productName} ({item.variantLabel}) x{item.quantity}
                {item.isGift && (
                  <svg className="w-3.5 h-3.5 ml-1 inline-block text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18C2.754 7.5 2.25 8.004 2.25 8.625v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                )}
              </span>
              <span className="font-medium text-text-dark flex-shrink-0">
                R$ {formatCurrency((item.promotionalPrice ?? item.price) * item.quantity)}
              </span>
            </div>
          ))}
        </div>
        <div className="border-t border-champagne pt-2 flex justify-between font-bold text-text-dark">
          <span>Total</span>
          <span>R$ {formatCurrency(total)}</span>
        </div>
      </div>

      {guestInfo && (
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h2 className="text-sm font-semibold text-text-dark mb-2">Seus dados</h2>
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between">
              <span className="text-text-medium">Nome</span>
              <span className="font-medium text-text-dark">{guestInfo.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Celular</span>
              <span className="font-medium text-text-dark">{guestInfo.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Bairro</span>
              <span className="font-medium text-text-dark">{guestInfo.neighborhood}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Cidade</span>
              <span className="font-medium text-text-dark">{guestInfo.city}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
        <h2 className="text-sm font-semibold text-text-dark mb-2">Vendedora</h2>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne flex items-center justify-center text-chocolate font-bold">
            {seller.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-text-dark">{seller.name}</p>
            <p className="text-xs text-text-light">{seller.whatsAppPhone}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
        <h2 className="text-sm font-semibold text-text-dark mb-2">Formas de pagamento</h2>
        <div className="flex flex-col gap-1">
          {paymentEntries.map((e, idx) => (
            <div key={idx} className="flex justify-between gap-2 text-sm">
              <span className="text-text-medium min-w-0 break-words">
                {PaymentMethodLabel[e.paymentMethod]}
                {e.paymentMethod === PaymentMethod.CreditCard && e.installments === 2 && ` — 2x de R$ ${formatCurrency(e.amountPaid / 2)}`}
              </span>
              <span className="font-medium text-text-dark flex-shrink-0">
                R$ {formatCurrency(e.amountPaid)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-text-dark mb-1">
          Observação (opcional)
        </label>
        <textarea
          value={observation}
          onChange={(e) => onObservationChange(e.target.value)}
          placeholder="Ex: precisa de embrulho para presente..."
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white text-sm focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate resize-none"
        />
      </div>

      <div className="bg-champagne-light rounded-xl p-3 text-xs text-text-medium mb-4">
        Ao confirmar, a vendedora receberá um resumo via WhatsApp e entrará em contato para finalizar o pagamento e a entrega.
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" disabled={submitting}>
          Voltar
        </Button>
        <Button
          onClick={onSubmit}
          loading={submitting}
          className="flex-1"
        >
          Confirmar pedido
        </Button>
      </div>
    </div>
  );
}
